/**
 * CL-STORAGE-001A — Classify Storage SDK failures without exposing SDK text,
 * paths, or tokens to portal users.
 */

function safeStr(value) {
  return String(value ?? "").trim();
}

function blobOf(err) {
  const code = safeStr(err?.code).toLowerCase();
  const message = safeStr(err?.message).toLowerCase();
  const name = safeStr(err?.name).toLowerCase();
  return `${code} ${message} ${name}`;
}

export const STORAGE_ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: "unauthenticated",
  UNAUTHORIZED: "unauthorized",
  OBJECT_NOT_FOUND: "object_not_found",
  NETWORK_CORS: "network_cors",
  RETRY_LIMIT: "retry_limit",
  TIMEOUT: "timeout",
  UNAVAILABLE: "unavailable",
});

export function classifyStorageSdkError(err) {
  const blob = blobOf(err);
  if (!blob.trim() && !err) return STORAGE_ERROR_CODES.UNAVAILABLE;
  if (blob.includes("unauthenticated") || blob.includes("auth/")) {
    return STORAGE_ERROR_CODES.UNAUTHENTICATED;
  }
  if (
    blob.includes("unauthorized") ||
    blob.includes("permission-denied") ||
    blob.includes("forbidden") ||
    blob.includes("permission denied")
  ) {
    return STORAGE_ERROR_CODES.UNAUTHORIZED;
  }
  if (blob.includes("object-not-found") || /\bnot-found\b/.test(blob) || blob.includes("404")) {
    return STORAGE_ERROR_CODES.OBJECT_NOT_FOUND;
  }
  if (blob.includes("retry-limit-exceeded") || blob.includes("max retry")) {
    return STORAGE_ERROR_CODES.RETRY_LIMIT;
  }
  if (
    blob.includes("timed out") ||
    blob.includes("timeout") ||
    blob.includes("aborterror") ||
    safeStr(err?.code) === "preview_timeout"
  ) {
    return STORAGE_ERROR_CODES.TIMEOUT;
  }
  if (
    blob.includes("cors") ||
    blob.includes("failed to fetch") ||
    blob.includes("network") ||
    blob.includes("load failed")
  ) {
    return STORAGE_ERROR_CODES.NETWORK_CORS;
  }
  return STORAGE_ERROR_CODES.UNAVAILABLE;
}

export const PORTAL_PREVIEW_ERROR_COPY =
  "We couldn’t load this PDF. The file may not have finished uploading. Try again or contact the project administrator.";

export const PORTAL_PREVIEW_TIMEOUT_COPY =
  "This PDF is large and took too long to preview in the browser. Use Open or Download — the file is available.";

export const ADMIN_PREVIEW_ERROR_COPY =
  "The file record exists, but its uploaded PDF could not be verified. No replacement or repair was performed.";

export const PORTAL_OPEN_ERROR_COPY = PORTAL_PREVIEW_ERROR_COPY;

export const ADMIN_UPLOAD_FAILURE_COPY =
  "The PDF could not be stored. The overall package was not published. Try again.";

export function previewErrorCopy({ audience = "portal", errorCode = "" } = {}) {
  if (safeStr(audience).toLowerCase() === "admin") return ADMIN_PREVIEW_ERROR_COPY;
  if (safeStr(errorCode).toLowerCase() === STORAGE_ERROR_CODES.TIMEOUT) return PORTAL_PREVIEW_TIMEOUT_COPY;
  return PORTAL_PREVIEW_ERROR_COPY;
}

export function isSafeUserFacingStorageCopy(text) {
  const raw = safeStr(text).toLowerCase();
  if (!raw) return false;
  if (raw.includes("firebase storage")) return false;
  if (raw.includes("storage/")) return false;
  if (raw.includes("retry-limit")) return false;
  if (raw.includes("pmprojectfiles/")) return false;
  if (raw.includes("token=")) return false;
  return true;
}
